import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiCheck, FiX, FiSave, FiUser, FiList } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api';

const colors = {
  primary: '#875A7B',
  light: '#F8F5F7',
  text: '#333333',
  border: '#D9C7D5',
};

export default function RoleEtapesSelector() {
  const [roles, setRoles] = useState([]);
  const [allEtapes, setAllEtapes] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedEtapes, setSelectedEtapes] = useState([]);

  // Fetch roles
  useEffect(() => {
    api.get('/parametrages/roles')
      .then(res => {
        // normalize payload
        const data = res.data.roles ?? res.data.data ?? res.data;
        setRoles(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Erreur GET /roles :', err);
        toast.error('Impossible de charger la liste des rôles');
      });
  }, []);

  // Fetch all étapes
  useEffect(() => {
    api.get('/parametrages/etapes')
      .then(res => {
        const data = res.data.etapes ?? res.data.data ?? res.data;
        setAllEtapes(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Erreur GET /etapes :', err);
        toast.error('Impossible de charger la liste des étapes');
      });
  }, []);

  // When a role is selected, prefill its étapes
  const handleRoleClick = (role) => {
    setSelectedRole(role);
    const ids = Array.isArray(role.etapes)
      ? role.etapes.map(e => e.id)
      : [];
    setSelectedEtapes(ids);
  };

  // Toggle one étape on/off
  const toggleEtape = (etapeId) => {
    setSelectedEtapes(prev =>
      prev.includes(etapeId)
        ? prev.filter(id => id !== etapeId)
        : [...prev, etapeId]
    );
  };

  // Save changes via API, show toast on success/error
  const handleSave = () => {
    if (!selectedRole) {
      toast.warn('Veuillez sélectionner un rôle d’abord');
      return;
    }
    api.post(
      `/parametrages/roles/${selectedRole.id}/etapes`,
      { etapes_ids: selectedEtapes }
    )
    .then(() => {
      toast.success('Modifications enregistrées avec succès');
      // update local roles state so UI stays in sync
      setRoles(prev =>
        prev.map(r =>
          r.id === selectedRole.id
            ? { ...r, etapes: allEtapes.filter(e => selectedEtapes.includes(e.id)) }
            : r
        )
      );
    })
    .catch(err => {
      console.error('Erreur post /roles/{id}/etapes :', err);
      toast.error('Erreur lors de la sauvegarde');
    });
  };

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: colors.light }}>
      {/* ToastContainer to render toasts */}
      <ToastContainer position="top-right" autoClose={500} />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <div className="p-3 rounded-lg mr-4" style={{ backgroundColor: colors.primary, color: 'white' }}>
            <FiUser size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: colors.primary }}>
              Gestion des Autorisations
            </h1>
            <p className="text-sm" style={{ color: colors.text }}>
              Attribution des étapes aux rôles
            </p>
          </div>
        </div>

        {/* Role selection */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8" style={{ border: `1px solid ${colors.border}` }}>
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <FiList className="mr-2" style={{ color: colors.primary }} />
            <span style={{ color: colors.primary }}>Sélectionnez un rôle</span>
          </h2>
          <div className="flex flex-wrap gap-3">
            {roles.map(role => (
              <button
                key={role.id}
                onClick={() => handleRoleClick(role)}
                className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center ${
                  selectedRole?.id === role.id ? 'text-white shadow-md' : 'hover:bg-gray-50'
                }`}
                style={{
                  backgroundColor: selectedRole?.id === role.id ? colors.primary : 'white',
                  border: `1px solid ${selectedRole?.id === role.id ? colors.primary : colors.border}`
                }}
              >
                {role.nom}
              </button>
            ))}
          </div>
        </div>

        {/* Étapes toggles */}
        {selectedRole && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8" style={{ border: `1px solid ${colors.border}` }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: colors.primary }}>
              Étapes disponibles pour : <span className="font-bold">{selectedRole.nom}</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allEtapes.map(etape => (
                <div
                  key={etape.id}
                  onClick={() => toggleEtape(etape.id)}
                  className={`p-3 rounded-lg cursor-pointer flex items-center transition-all duration-200`}
                  style={{
                    backgroundColor: selectedEtapes.includes(etape.id) ? `${colors.primary}10` : 'white',
                    border: `1px solid ${selectedEtapes.includes(etape.id) ? colors.primary : colors.border}`
                  }}
                >
                  <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 ${
                    selectedEtapes.includes(etape.id) ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {selectedEtapes.includes(etape.id) ? <FiCheck size={14} /> : <FiX size={14} />}
                  </div>
                  <span style={{ color: colors.text }}>{etape.nom}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save button */}
        {selectedRole && (
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              className="px-6 py-3 rounded-lg font-medium flex items-center transition-all duration-200 text-white hover:shadow-md"
              style={{ backgroundColor: colors.primary }}
            >
              <FiSave className="mr-2" /> Enregistrer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
